<template>
  <b-container fluid style="margin: 50px 0;">
    <div v-if="hasApiCallFailed">
      <something-went-wrong :text="$t('easyAllocation')" fromEasy :responseStatus="responseStatus" />
    </div>
    <template v-else-if="loading">
      <loading />
    </template>
    <template v-else-if="!hasNoRecords">
      <b-row id="page-top">
        <div class="col-12"><h3>{{ $t('easyAllocation') }}</h3></div>
        <div class="col-12">
          <div class="card card-body bg-light license-info-container">
            <p>
              <span>{{ $tc('orderNumber', 1) }}: <b>{{ orderNumber }}</b></span>
              <span class="pipe">|</span>
              <span>{{ $t('orderDate') }}: <b>{{ orderDate }}</b></span>
            </p>
          </div>
          <b-row style="padding-bottom: 20px;">
            <div class="col-12 col-md-3 cta-btn-container">
              <b-button
                variant="primary"
                size="lg"
                class="cta-btn"
                :disabled="isAllocateDisabled"
                @click="allocateModal = true"
                :title="selectedLicenses.length === 0 ? $t('noSelectionInfoText') : ''"
              >
                {{ $t('allocateLicense') }}
              </b-button>
            </div>
            <div class="col-12 d-sm-block d-md-none options-btn-container">
              <a href="#" @click.stop.prevent="showOptions = !showOptions">
                {{ $t('viewHideOptions', { action: !showOptions ? $t('view') : $tc('hide', 1) } ) }}
                <i :class="`fa fa-chevron-${!showOptions ? 'down' : 'up'}`" />
              </a>
            </div>
            <div :class="['col-12 col-md-4 offset-md-5 options-container', { show: showOptions }]">
              <div class="row">
                <div class="col-8">
                  <fieldset class="form-group">
                    <legend>{{ $t('sortBy') }}</legend>
                    <div class="select-container">
                      <b-select
                        v-model="sortOption"
                        :options="sortOptions"
                        @change="handleSortChange"
                      />
                    </div>
                  </fieldset>
                </div>
                <div class="col-4">
                  <fieldset class="form-group">
                    <legend>{{ $t('show') }}</legend>
                    <div class="select-container">
                      <b-select
                        v-model="pageSizeOption"
                        @change="handlePageNumberChange"
                        :options="pageSizeOptions"
                      />
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </b-row>
        </div>
      </b-row>
      <b-container fluid>
        <b-row class="all-licenses-table-header vertical-padding left-spacing">
          <div class="col checkbox-col">
            <b-checkbox @change="toggleAllLicensesSelection" v-model="areAllLicensesSelected" />
          </div>
          <div class="col">
            <div class="row">
              <div class="col indent-text">{{ $t('productName') }}</div>
              <div class="d-none d-md-block col dynamic-column-select-container header">
                {{ dynamicColumnHeader }}
                &nbsp;&nbsp;&nbsp;
                <i class="fa fa-caret-down" />
                <b-select
                  :options="dynamicColumnOptions"
                  v-model="dynamicColumnOption"
                  class="dynamic-column-select"
                />
              </div>
              <div class="d-none d-md-block col allocate-col text-right">{{ $t('allocated') }}</div>
            </div>
          </div>
        </b-row>
        <template v-for="(license, i) in licenses">
          <b-row :key="license.ProductName + i" :class="[getTableRowClasses(license, i)]">
            <div class="col checkbox-col" >
              <b-checkbox
                @change="toggleLicenseSelection(license)"
                :checked="isLicenseSelected(license)"
                :class="isLicenseInvalid(license) ? 'error-checkbox' : ''"
              />
            </div>
            <div class="col product-name-col">
              <div class="row">
                <div
                  class="col product-name-col indent-text"
                  :title="`${license.ProductName}`"
                >
                  <span :title="license.ProductName">
                    {{ license.ProductName }}
                  </span>
                  <span v-if="isLicenseNew(license)" class="success-text">{{ $t('new') }}</span>
                  <span v-else-if="isLicenseExpiringLow(license)" class="danger-text">
                    {{ $tc('expiring', 1) }}
                  </span>
                  <span v-else-if="isLicenseExpiringHigh(license)" class="warning-text">
                    {{ $tc('expiring', 1) }}
                  </span>
                  <p v-if ="isLicenseInvalid(license)" class= "ex">
                    {{ $t('multipleSelectionHostTypeError', { hostType }) }}
                  </p>
                </div>
                <div
                  class="d-none d-md-block col dynamic-column-select-container truncate-col"
                  :title="dynamicColumnOption === 'HostValue' ? dynamicColumnItem(license) : ''"
                >
                  {{ dynamicColumnItem(license) }}
                </div>
                <div class="d-none d-md-block col allocate-col text-right">
                  {{ getAllocatedText(license) }}
                </div>
              </div>
            </div>
          </b-row>
        </template>
        <b-row class="all-licenses-table-footer">
          <div class="col-12 text-center">
            <div class="pagination-container">
              <b-pagination
                :value="currentPage"
                @change="handlePageChange"
                :limit="6"
                :per-page="pageSizeOption"
                hide-goto-end-buttons
                :prev-text="$t('paginationPrevText')"
                :next-text="$t('paginationNextText')"
                :total-rows="totalFilteredSortedLicenses"
                ref="pagination"
              />
            </div>
          </div>
        </b-row>
        <b-row>
          <div class="col-12">
            <p>
              <b>{{ $t('easyQuestion1') }}</b><br />
              {{ $t('easyAnswer1') }}
            </p>
            <p>
              <b>{{ $t('easyQuestion2') }}</b><br />
              <span
                v-html="$t('easyAnswer2', {
                  citrixLicensingDocumentationUrl: UrlBuilder.citrixLicensingDocumentationUrl,
                  docsUrl: UrlBuilder.docsUrl
                })"
              />
            </p>
          </div>
        </b-row>
        <b-row>
          <div class="col-12 text-center">
            <b-button
              class="outline-btn btn-c-outline"
              size="lg"
              @click="handleSignInClick"
            >
              {{ $t('signInToMyAccount') }}
            </b-button>
          </div>
        </b-row>
      </b-container>
    </template>
    <template v-if="hasNoRecords">
      <b-row class="no-records-container">
        <h3>{{ $t('licenseFullyAllocated') }}</h3>
      </b-row>
    </template>
    <b-modal v-model="showPagingWarning" @hide="handleModalClose">
      <div slot="modal-title">
        {{ $t('bulkActions') }}
      </div>
      <b-container>
        <b-row class="text-center">
          <div class="col-12">
            <h5 class="danger-text">{{ $t('bulkActionErrorTitle') }}</h5>
            <h6 v-html="$t('bulkActionErrorInfo')" />
          </div>
        </b-row>
        <b-row class="buttons-container">
          <div class="col-12">
            <b-button
              style="margin: 30px auto 10px; display: block;"
              variant="primary"
              size="lg"
              @click="handleModalClose"
            >
              {{ $t('bulkActionCtaButtonText') }}
            </b-button>
          </div>
          <div class="col-12 text-center">
            <a href="#" @click.stop.prevent="clearLicensesAndPaginate">
              {{ $t('bulkActionSecondaryButtonText') }}
            </a>
          </div>
        </b-row>
      </b-container>
      <div slot="modal-footer" />
    </b-modal>
    <allocation-modal
      isFromEasyPage
      :modal="allocateModal"
      :closeModal="closeAllocateModal"
      :selectedLicenses="selectedLicenses"
      :getUpdatedLicenses="getLicenses"
      :customHeader="customHeader"
    />
  </b-container>
</template>

<script>
  import Vue from 'vue'
  import moment from 'moment'

  import Datepicker from './Datepicker'
  import Loading from './Loading'
  import SomethingWentWrong from './SomethingWentWrong'
  import AllocationModal from './AllocationModal'

  export default {
    name: 'EasyAllocation',
    components: {
      'datepicker': Datepicker,
      'loading': Loading,
      'something-went-wrong': SomethingWentWrong,
      'allocation-modal': AllocationModal
    },
    created () {
      Vue.AppInsights.trackPageView('Easy allocate page', '/easy')
    },
    data () {
      return {
        loading: false,
        hasApiCallFailed: false,
        currentPage: 1,
        orderNumber: null,
        orderDate: null,
        showOptions: false,
        sortOption: '',
        sortOptions: [
          { value: 'ProductNameAsc', text: this.$t('productAsc') },
          { value: 'ProductNameDesc', text: this.$t('productDesc') }
        ],
        pageSizeOption: 25,
        pageSizeOptions: [25, 50, 75, 100],
        licenseCreationValue: null,
        areAllLicensesSelected: false,
        allLicenses: [],
        selectedLicenses: [],
        totalFilteredSortedLicenses: 0,
        hasError: false,
        pageToLoad: null,
        showPagingWarning: false,
        shouldDisableAllocate: false,
        dynamicColumnOption: 'SerialNumber',
        dynamicColumnOptions: [
          { value: 'SerialNumber', text: this.$t('licenseAccessCode') },
          { value: 'SAPSalesOrderNumber', text: this.$tc('orderNumber', 2) }
        ],
        hasNoRecords: false,
        allocateModal: false,
        responseStatus: null
      }
    },
    computed: {
      licenses () {
        const startIndex = (this.currentPage - 1) * this.pageSizeOption
        const endIndex = (this.currentPage * this.pageSizeOption)
        const licenses = this.getSortedLicenses([...this.allLicenses])
        this.totalFilteredSortedLicenses = licenses.length
        return licenses.slice(startIndex, endIndex)
      },
      isAllocateDisabled () {
        return this.selectedLicenses.length === 0 ||
          this.hasError ||
          this.shouldDisableAllocate
      },
      dynamicColumnHeader () {
        return this.dynamicColumnOptions.find(o => o.value === this.dynamicColumnOption).text
      },
      hostType () {
        return this.selectedLicenses.length > 0
          ? this.$t(this.selectedLicenses[0].HostType.toLowerCase())
          : ''
      },
      customHeader () {
        return this.$route.params.id || ''
      },
      authToken () {
        const authToken = JSON.parse(sessionStorage.getItem('auth_token') || JSON.stringify({}))
        return authToken.access_token || ''
      }
    },
    mounted () {
      if (this.$route.params.id) {
        this.getLicenses()
      } else {
        this.$router.push('/')
      }
    },
    methods: {
      validateMultipleSelection () {
        let hostType
        if (!this.selectedLicenses.length) return
        hostType = this.selectedLicenses[0].HostType
        this.selectedLicenses = this.selectedLicenses.map(l => {
          if (l.HostType === hostType) {
            delete l.isValid
            return l
          } else {
            return { ...l, isValid: false }
          }
        })
        this.hasError = this.selectedLicenses.some(l => l.hasOwnProperty('isValid'))
        this.shouldDisableAllocate = this.selectedLicenses
          .some(l => l.RemainingUserCount === 0)
      },
      isLicenseInvalid (license) {
        return (this.isLicenseSelected(license, true) || {}).hasOwnProperty('isValid')
      },
      handleSortChange (sort) {
        this.sortOption = sort
        this.currentPage = 1
        this.handleInteraction('sort')
      },
      handlePageNumberChange (pageNumber) {
        this.pageSizeOption = pageNumber
        this.currentPage = 1
      },
      handleDateChange ({ fromDate, toDate }) {
        this.fromDate = fromDate
        this.toDate = toDate
        if (this.fromDate && this.toDate) {
          this.currentPage = 1
        }
      },
      toggleAllLicensesSelection () {
        this.selectedLicenses = this.areAllLicensesSelected
          ? []
          : [...this.licenses]
        this.validateMultipleSelection()
      },
      toggleLicenseSelection (license) {
        if (!this.selectedLicenses.length || !this.isLicenseSelected(license)) {
          this.selectedLicenses.push(license)
        } else {
          this.selectedLicenses = this.selectedLicenses
            .filter(l => l.ActivationID !== license.ActivationID)
        }
        this.areAllLicensesSelected = this.selectedLicenses.length === this.licenses.length
        this.validateMultipleSelection()
      },
      getTableRowClasses (item, i) {
        const classes = ['vertical-padding', 'table-row', i % 2 === 1 ? 'odd-row' : '']
        if (this.isLicenseNew(item)) {
          classes.push('new-license-row')
        } else if (this.isLicenseExpiringLow(item)) {
          classes.push('expiring-license-row-low')
        } else if (this.isLicenseExpiringHigh(item)) {
          classes.push('expiring-license-row-high')
        } else if (this.isLicenseInvalid(item)) {
          classes.push('error-row', 'left-spacing')
        } else {
          classes.push('left-spacing')
        }
        return classes
      },
      calculateDayDifference (date, expiring = false) {
        let isDateLessThanToday, from, to, number
        number = expiring ? 0 : 1
        date = new Date(date)
        date = date.setTime(
          Date.UTC(
            date.getFullYear(), date.getMonth(), date.getDate(),
            date.getHours(), date.getMinutes(), date.getSeconds()
          )
        )
        isDateLessThanToday = Date.now() < new Date(date).getTime()
        if (moment().isSame(moment(date), 'day')) {
          return 0
        }
        if (expiring && !isDateLessThanToday) {
          return -1
        }
        from = isDateLessThanToday ? moment(date) : moment()
        to = isDateLessThanToday ? moment().subtract(1, 'days') : moment(date)
        return from.diff(to, 'days') + number
      },
      isLicenseNew (item) {
        const dayDifference = this.calculateDayDifference(item.StartDate)
        return dayDifference >= 0 && dayDifference <= 30
      },
      isLicenseExpiringLow (item) {
        const dayDifference = this.calculateDayDifference(item.ExpirationDate, true)
        return dayDifference >= 0 && dayDifference <= 29
      },
      isLicenseExpiringHigh (item) {
        const dayDifference = this.calculateDayDifference(item.ExpirationDate, true)
        return dayDifference > 29 && dayDifference <= 89
      },
      getAllocatedText ({ RemainingUserCount, UserCount }) {
        return RemainingUserCount > UserCount
          ? `${UserCount} of ${UserCount}`
          : `${UserCount - RemainingUserCount} of ${UserCount}`
      },
      handleInteraction (interactionType) {
        switch (interactionType) {
          case 'exportToCSV':
            Vue.AppInsights.trackEvent('Export to CSV', {
              eventType: 'click',
              page: 'Easy allocation'
            })
            break
          case 'sort':
            Vue.AppInsights.trackEvent('Sort', {
              eventType: 'change',
              sortValue: this.sortOption,
              page: 'Easy allocation'
            })
            break
          default:
            break
        }
      },
      getSortedLicenses (licenses) {
        switch (this.sortOption) {
          case 'ProductNameAsc':
            return licenses.sort((a, b) => a.ProductName < b.ProductName ? -1 : 1)
          case 'ProductNameDesc':
            return licenses.sort((a, b) => a.ProductName > b.ProductName ? -1 : 1)
          default:
            return licenses
        }
      },
      setLicenseInfo () {
        let minStartDate
        let maxStartDate
        this.allLicenses.forEach((l, i) => {
          if (i === 0) {
            minStartDate = new Date(this.allLicenses[0].StartDate).getTime()
            maxStartDate = new Date(this.allLicenses[this.allLicenses.length - 1].StartDate)
              .getTime()
            this.orderNumber = this.allLicenses[0].SAPSalesOrderNumber
            this.orderDate = moment(this.allLicenses[0].StartDate).format('DD MMM YYYY')
          }
          const timestamp = new Date(l.StartDate).getTime()
          minStartDate = timestamp < minStartDate ? timestamp : minStartDate
          maxStartDate = timestamp > maxStartDate ? timestamp : maxStartDate
        })
        this.licenseCreationValue = [
          moment(minStartDate).format('MM/DD/YYYY'),
          moment(maxStartDate).format('MM/DD/YYYY')
        ]
      },
      handleExportLinkClick () {
        this.bottle.post(this.UrlBuilder.exportCsvUrl, this.requestBody)
          .then(console.log)
          .catch(console.error)
        this.handleInteraction('exportToCSV')
      },
      isLicenseSelected (license, returnItem = false) {
        const selectedLicense = this.selectedLicenses
          .find(val => val.ActivationID === license.ActivationID)
        return returnItem ? selectedLicense : !!selectedLicense
      },
      paginate (page) {
        this.currentPage = page
        window.scrollTo(0, document.getElementById('page-top').offsetTop)
      },
      handlePageChange (currentPage) {
        if (this.selectedLicenses.length === 0) {
          this.paginate(currentPage)
        } else {
          this.pageToLoad = currentPage
          this.showPagingWarning = true
        }
      },
      handleModalClose () {
        this.showPagingWarning = false
        this.$refs.pagination.currentPage = this.currentPage
      },
      clearLicensesAndPaginate () {
        this.selectedLicenses = []
        this.handleModalClose()
        this.paginate(this.pageToLoad)
      },
      dynamicColumnItem (license) {
        switch (this.dynamicColumnOption) {
          case 'SAPSalesOrderNumber':
            if (!license.LicenseItemNumber) {
              return `${license.SAPSalesOrderNumber}/${this.getLicenseItemNumber(license)}`
            }
            return `${license.SAPSalesOrderNumber}/${license.LicenseItemNumber}`
          case 'HostValue':
            return license.HostValue || ''
          case 'SerialNumber':
          default:
            return license.SerialNumber || ''
        }
      },
      getLicenseItemNumber ({ ActivationID }) {
        return ActivationID.match(/^.*-.*-(.*)/)[1]
      },
      closeAllocateModal () {
        this.allocateModal = false
        this.selectedLicenses = []
        this.areAllLicensesSelected = false
      },
      getLicenses () {
        this.loading = true
        this.bottle.get(this.UrlBuilder.easyUrl, {
          headers: {
            'X-Easyheader': this.customHeader
          }
        })
          .then(({ data, status }) => {
            console.log('inside then')
            this.responseStatus = status
            this.allLicenses = data.Licenses || []
            this.allLicenses = this.allLicenses.sort((a, b) => {
              return Number(a.LicenseItemNumber) - Number(b.LicenseItemNumber)
            })
            this.hasNoRecords = this.allLicenses.length === 0
            this.$store.commit('setCustomerId', data.CustomerID)
            this.setLicenseInfo()
            this.loading = false
          })
          .catch((error) => {
            this.responseStatus = error.response.status
            this.loading = false
            this.hasApiCallFailed = true
            this.loggly.log({
              errorMessage: 'Failed to fetch easy allocation licenses',
              errorObject: error,
              errorStack: error.stack
            })
          })
      },
      handleSignInClick () {
        window.open(this.UrlBuilder.easyPageSignInUrl, '_self')
      }
    }
  }
</script>

<style scoped lang="scss">
  @import '../styles/variables.scss';

  a {
    color: $primary;
  }

  .license-info-container {
    padding-top: 5px;
    padding-bottom: 5px;
    margin: 10px 0 20px;
  }

  .license-info-container p {
    display: inline;
    margin: 5px 0;
  }

  .license-info-container span {
    display: block;
  }

  .license-info-container .pipe {
    display: none;
  }

  .cta-btn-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 66px;
  }

  .cta-btn {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 5px;
    padding-bottom: 5px;
    border-radius: 25px;
    height: 50px;
    font-size: 20px;
  }

  .options-btn-container {
    text-align: center;
    margin: 20px 0 0;
    font-size: 18px;
  }

  .options-btn-container a,
  .options-btn-container a:hover,
  .options-btn-container a:focus,
  .options-btn-container a:active {
    text-decoration: none;
    color: inherit;
  }

  .options-container {
    min-height: 0;
    height: 0;
    opacity: 0;
    display: none;
  }

  .options-container.show {
    height: auto;
    opacity: 1;
    display: block;
    transition: height 0.5s;
  }
  
  fieldset {
    border: 1px solid $lightGray;
    border-radius: 5px;
    padding: 10px 0 0;
    width: 100%;
  }

  fieldset > legend {
    padding: 0 10px;
    margin-left: 10px;
    width: auto;
    font-size: 14px;
    color: $darkGray;
    margin-bottom: 0;
    z-index: $legendZIndex;
    position: relative;
    background-color: white;
  }

  .select-container {
    margin: -20px 0 0;
  }

  select {
    border: none !important;
    height: 30px;
    width: 100% !important;
    text-indent: 10px;
  }

  select:hover,
  select:focus,
  select:active {
    outline: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }

  .form-control[readonly] {
    background-color: white !important;
  }
  
  .all-licenses-table-header {
    background-color: $lightGray;
    margin-bottom: 10px;
  }

  .vertical-padding {
    padding-top: 7px;
    padding-bottom: 7px;
  }

  .table-row {
    margin-bottom: 10px;
    border: 1px solid $lightGray;
    border-radius: 5px;
  }

  .new-license-row {
    border-left-width: 10px;
    border-left-color: $success;
  }

  .expiring-license-row-low {
    border-left-width: 10px;
    border-left-color: $danger;
  }

  .expiring-license-row-high {
    border-left-width: 10px;
    border-left-color: $warning;
  }

  .left-spacing {
    padding-left: 10px;
  }

  .success-text {
    color: $success;
  }

  .danger-text {
    color: $danger;
  }

  .warning-text {
    color: $warning;
  }
  
  .all-licenses-table-footer {
    margin-top: 20px;
  }
  
  .export-link {
    display: inline-block;
    margin-left: -15px;
    margin-top: 15px;
  }

  .pagination-container {
    margin-right: -15px;
    display: inline-block;
  }

  .custom-select:disabled {
    background-color: white;
    cursor: not-allowed;
    border: none;
  }

  .custom-control-inline {
    margin-right: 0;
    padding: 3px;
  }

  .checkbox-col {
    flex: 0 0 16px;
  }

  .product-name-col {
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .dynamic-column-select-container {
    position: relative;
    flex: 0 0 340px;
    max-width: 340px;
  }

  .dynamic-column-select-container:not(.header) {
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .dynamic-column-select {
    opacity: 0;
    position: absolute;
    left: 0;
    top: 1px;
    cursor: pointer;
  }

  .indent-text {
    margin-left: -10px;
  }

  .allocate-col {
    flex: 0 0 150px;
  }

  .odd-row {
    background-color: $lightPrimary;
  }

  .no-records-container {
    background-color: $lightGray;
    padding: 20px;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .no-records-container h3 {
    font-weight: 400;
  }

  .outline-btn {
    margin-top: 30px;
    border-radius: 25px;
    padding-left: 50px;
    padding-right: 50px;
  }

  @media (min-width: 768px) {
    .license-info-container span {
      display: none;
    }

    .license-info-container span {
      display: inline;
    }

    .license-info-container .pipe {
      display: inline;
    }

    .cta-btn {
      height: 39px;
      font-size: 18px;
      margin-top: -6px;
    }

    .options-container {
      height: auto;
      opacity: 1;
      display: block;
    }

    .pagination-container {
      display: block;
      float: right;
    }
    .multiple-error {
      border: 1px solid #D8000C;
    }
    p.ex {
      color: red;
      white-space: normal;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/EasyAllocation.vue